<script lang="ts" setup>
import { computed, ref, toRefs, watch } from 'vue';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import Avatar from '@/components/common/atoms/avatar.vue';
import MentionAutocompleteTextarea from '@/components/common/molecules/mention-autocomplete-textarea.vue';
import { Mention } from '@/types';
import { useUserInfo } from '@/utils/swr';

const props = defineProps<{
  isPostingComment: boolean;
}>();
const { isPostingComment } = toRefs(props);

type Submit = { content: string; mention: Mention };
const emit = defineEmits<{
  close: [];
  submit: [Submit];
}>();

const disabledButton = ref(false);
const { data: myInfo } = useUserInfo();

const hideDialog = () => {
  emit('close');
  return true;
};
const commentTextRef = ref<InstanceType<typeof MentionAutocompleteTextarea>>();

const commentContent = ref('');

watch(
  () => commentTextRef.value?.trimmedComment ?? '',
  newContent => {
    if (commentTextRef.value && commentContent.value !== newContent) {
      commentContent.value = newContent;
    }
  },
);

const isPostEnabled = computed(() => {
  return (
    !disabledButton.value &&
    commentContent.value.length > 0 &&
    !isPostingComment.value
  );
});

const callPostComment = () => {
  if (commentTextRef.value?.trimmedComment !== '') {
    commentTextRef.value?.postComment();
  }
};

const post = async () => {
  if (!commentTextRef.value) return;
  const comment = commentTextRef.value.trimmedComment;
  const mention = commentTextRef.value.mention;
  if (comment.length === 0) return;

  emit('submit', { content: comment, mention });
};
</script>

<template>
  <DgrDialog
    :open="true"
    :close-interceptor-handler="hideDialog"
    @cancel="hideDialog"
    :close-on-escape="false"
    :with-header-divider="false"
  >
    <template #description>
      <div class="m-body ccwam-body">
        <div class="ccwam-myinfo-area">
          <router-link
            v-if="myInfo"
            :to="`/users/${myInfo.id}`"
            custom
            v-slot="{ href, navigate }"
          >
            <a
              :href="href"
              @click="
                hideDialog();
                navigate($event);
              "
            >
              <div class="ccwam-myinfo">
                <Avatar :image-url="myInfo.image_url"></Avatar>
                <div class="c-title-bold--m user-name">
                  {{ myInfo.user_name }}
                </div>
              </div>
            </a>
          </router-link>
        </div>
        <MentionAutocompleteTextarea
          class="comment-text-area ccwam-text-area"
          ref="commentTextRef"
          placeholder="コメントを書く（URLのみの投稿はできません）"
          @post-comment="post"
          :disabled="isPostingComment"
          :min-height="100"
          :show-url-preview="true"
          @update-button-status="disabledButton = $event"
        ></MentionAutocompleteTextarea>
      </div>
    </template>
    <template #footer>
      <button
        class="c-outlineBtn c-btn--auto c-outlineBtn--secondary"
        @click="hideDialog"
      >
        キャンセル
      </button>
      <button
        class="c-btn c-btn--auto c-btn--AnewsPrimary"
        :disabled="!isPostEnabled"
        :class="{ disabled: !isPostEnabled }"
        @click="callPostComment"
      >
        投稿
      </button>
    </template>
  </DgrDialog>
</template>
<style lang="scss" scoped>
.ccwam-body {
  width: 616px;
  margin-bottom: -16px;

  .ccwam-myinfo-area {
    margin-bottom: 10px;
    .ccwam-myinfo {
      display: flex;
      align-items: center;
      .user-name {
        margin-left: 8px;
      }
    }
  }
}
</style>
